import type { ErrorCode } from '@good-season/schema';

/**
 * Error codes to copy. The client half of the rule that no raw error string
 * ever reaches a user.
 *
 * The API returns `{ error: { code, message, diagnostic } }`. Nothing in this
 * package ever renders `message`: it is the server's readable fallback for a
 * log line or a non-browser consumer, and it is written in a vacuum. The copy
 * below is the client's, which is what `packages/schema/src/errors.ts` says it
 * expects ("the CLIENT owns the real copy, themed and contextual").
 *
 * Voice, from the handoff, binding on every string here:
 *   - Say what happened, then what to do next.
 *   - Never apologize. No "Oops", no "Something went wrong", no "Sorry".
 *   - Plain verbs, sentence case, no filler, and no hype.
 *   - Address a busy adult standing in a parking lot.
 *
 * The table is typed `Record<ErrorCode, ErrorCopy>`, so adding a code to the
 * catalog without writing copy for it fails the build rather than shipping a
 * blank dialog.
 */

export interface ErrorCopy {
  /** What happened, in a few words. No trailing period: it is a heading. */
  readonly title: string;
  /** What to do next. One or two sentences. */
  readonly body: string;
}

export const ERROR_COPY: Readonly<Record<ErrorCode, ErrorCopy>> = {
  PUSH_PERMISSION_DENIED: {
    title: 'Game reminders are blocked',
    body: 'Allow notifications for Good Season in your device settings, then try again.',
  },
  PUSH_UNAVAILABLE: {
    title: 'Game reminders could not be enabled',
    body: 'Try again with a connection. If it still fails, contact support with the code below.',
  },
  // Deliberately identical to AUTH_LINK_EXPIRED. The server collapses invalid,
  // expired, and already-used into one answer so a guess cannot be graded, and
  // copy that distinguishes them would give back what the API withheld.
  AUTH_LINK_INVALID: {
    title: 'That sign-in link is used up',
    body: 'Request a new one and it will be in your inbox in a moment.',
  },
  AUTH_LINK_EXPIRED: {
    title: 'That sign-in link is used up',
    body: 'Request a new one and it will be in your inbox in a moment.',
  },
  AUTH_SESSION_EXPIRED: {
    title: 'You have been signed out',
    body: 'Sign in again to pick up where you left off.',
  },
  AUTH_REQUIRED: {
    title: 'Sign in to see this',
    // Not "your coach can send a new link": nothing lets a coach send one.
    // `requestSignInLink` is self-service and there is no password reset.
    body: 'Request a link from the sign-in screen if you need one.',
  },
  AUTH_SCOPE_EXCEEDED: {
    title: 'This link covers one game',
    body: 'Ask your coach for access to the rest of the season.',
  },
  AUTH_REAUTH_REQUIRED: {
    title: 'Sign in again first',
    body: 'A new password can be set within 15 minutes of signing in. Sign in again, then come straight back here.',
  },
  REVIEW_ACCOUNT_NO_PASSWORD: {
    title: 'This demo account cannot set a password',
    body: 'It signs in with the review credentials only. Everything else in the app is open to it as usual.',
  },

  FORBIDDEN: {
    title: 'Your role does not include this',
    /*
     * Four throw sites, and a coach can act on only one of them.
     *
     * Three are role decisions: this role may not read a roster, may not change
     * one, may not manage people. No route in the API changes a membership
     * role, so "ask a coach if you need it added" is the same false referral
     * corrected twice already, one entry below this in the same table. The
     * fourth is "no membership at all", which a coach genuinely can fix.
     *
     * NOT added to `NO_ONE_HERE_CAN_FIX`, because that list means nobody can
     * act and here somebody sometimes can. The copy covers both by asking a
     * question a coach can answer and naming the fallback for when the answer
     * does not help. Graded S2 by the fresh-context pass.
     */
    body: 'Ask a coach whether you are on this team. If you are, contact support.',
  },
  INVITE_PENDING: {
    title: 'Your invitation has not been opened yet',
    // A coach really can: Resend sits on the player's card (ADR-0070).
    body: 'Open the email your coach sent to join the team. If it is gone, ask your coach to send it again.',
  },
  STAFF_LINK_OWNER_ONLY: {
    title: 'Linking a coach as a parent is not open to your role',
    /*
     * The person who may is the club's Owner (ADR-0116), and they really can
     * act: the Owner's People and Guardians sheets carry the control. The
     * tripwire above refuses the role's title in this table, so the role is
     * named by what it does, and `LinkParentSheet` says it plainly.
     */
    body: 'Ask the person who runs the club to link the coach to the child.',
  },
  ROLE_NOT_IMPLEMENTED: {
    title: 'That role is not in use yet',
    /*
     * Two wrong answers before this one, and the second is the interesting one.
     *
     * "Choose another role" was written when nothing threw this, so nobody had
     * asked who reads it: a MEMBER holding a reserved role, not an admin
     * assigning one, and there is no role picker anywhere in the product.
     *
     * "Ask whoever set up your team to give you a coach or manager role"
     * replaced it, and is false for a reason that takes a grep to see. The only
     * `update(memberships)` in the entire API is the acceptance stamp in
     * `password-auth.ts`. No route anywhere changes a membership's role, so the
     * person who set the team up can do exactly nothing about this. It named a
     * real human instead of a made-up screen, which felt like an improvement
     * and was the same defect. Graded S2 by the fresh-context pass, which found
     * it sitting inside the table the "do not send them to somebody who cannot
     * act" rule had just been written over.
     */
    body: 'Nothing here will work until you are moved to another role, and that has to be done for you. Contact support.',
  },
  // There is deliberately no TENANT_MISMATCH. It was removed from the catalog
  // because its copy, which read "That belongs to a different team," confirms to
  // a caller that the id they guessed is real and lives in another
  // organization. A cross-tenant miss maps to NOT_FOUND instead.
  //
  // Worth noting how this surfaced: typing the table as
  // `Record<ErrorCode, ErrorCopy>` meant deleting a code from the schema broke
  // the build here. That is rubric E2 doing its job, and it is the argument for
  // the exhaustive record over a lookup with a fallback.

  NAME_INVALID: {
    title: 'The name could not be saved',
    body: 'Use 1 to 80 characters with at least one letter or number, and no invisible characters or long stacks of accent marks.',
  },
  NAME_OWNER_ONLY: {
    title: "You cannot change this person's name",
    body: 'Ask the person who runs your club, or ask them to change it themselves.',
  },
  VALIDATION_FAILED: {
    title: 'Some of that did not save',
    body: 'Check the highlighted fields and send it again.',
  },
  PLAYER_EXISTS: {
    title: 'That player is already on the roster',
    body: 'A player with that name is already on this roster. Open the roster to check before adding another player.',
  },
  JERSEY_TAKEN: {
    title: 'That number is taken',
    body: 'Another player on this season already wears it. Pick a different one.',
  },
  DUPLICATE_PLAYER: {
    title: 'That player is already on the roster',
    body: 'Merge the two records, or skip this one.',
  },
  ALREADY_EXISTS: {
    title: 'That already exists',
    body: 'Open the existing one instead of creating a second.',
  },
  /*
   * Deliberately vague about the reason, and specific about the next step.
   *
   * The address is held by an account that is closed. Saying so would report
   * the existence of a closed account to anybody who can type an email, and the
   * coach cannot act on it either way.
   *
   * NOT "ask an owner", which is what this said. Nothing in the product clears
   * `deleted_at` and the email unique index is global, so an owner can do
   * exactly nothing about it. That is the same false promise the People screen
   * was corrected for three rounds running, sitting two files over in the
   * shared table, and the fresh-context pass found it in the commit named after
   * removing it. Graded S2.
   *
   * "Use a different address" is a real action and comes first. Support is the
   * fallback for the case where it has to be that address, which is where the
   * repair genuinely lives.
   */
  ADDRESS_UNAVAILABLE: {
    title: 'That address cannot be used',
    body: 'Use a different email address. If it has to be that one, contact support.',
  },
  GUARDIAN_IS_STAFF: {
    title: "That person is already on this team's staff",
    // The Owner alone links staff as a parent (ADR-0116), named by what they
    // do: the tripwire above refuses the role's title.
    body: 'Ask the person who runs the club to link them as a parent.',
  },
  BROADCAST_COUNT_CHANGED: {
    title: 'The audience changed',
    body: 'Review the new count, then confirm again. Nothing was sent.',
  },
  BROADCAST_IN_PROGRESS: {
    title: 'This update is already going out',
    body: 'Wait for it to finish, then review the history list. Nothing was sent twice.',
  },
  BROADCAST_NOTHING_TO_SEND: {
    title: 'Nobody left to send to',
    body: 'Change the message to send a new update. Everyone in this audience has already had this one, or has opted out.',
  },
  BROADCAST_LIMIT: {
    title: 'A sending limit was reached',
    body: 'Nothing was sent. Try again after the limit resets, or send a smaller update.',
  },
  BROADCAST_NOT_READY: {
    title: 'Sending is not set up yet',
    body: 'Ask whoever runs the server to set the mail, signing and postal address settings, then try again. Nothing was sent.',
  },
  NOT_FOUND: {
    title: 'That is not here',
    body: 'It may have been removed, or the link may be out of date. Ask your coach to send it again.',
  },
  LEAGUE_CODE_INVALID: {
    title: 'That code does not match a league',
    body: 'Check it with the person who runs the league and enter it again.',
  },
  LEAGUE_WAITING: {
    title: 'The league has not confirmed your club yet',
    body: 'Ask the person who runs the league to confirm the club, then enter the code again.',
  },
  LEAGUE_REMOVED: {
    title: 'The league took this team off its list',
    body: 'Ask the person who runs the league if the team should be listed again. Entering the code again will not put it back.',
  },
  STALE_WRITE: {
    title: 'Someone else changed this',
    body: 'Reload to see their version, then make your change on top of it.',
  },
  DIVISION_NAME_TAKEN: {
    title: 'That division name is in use',
    body: 'Choose a different name for this division.',
  },
  DIVISION_NOT_EMPTY: {
    title: 'That division still has teams',
    body: 'Choose another division for its teams, then archive this division.',
  },
  LAST_CLUB_OWNER: {
    title: 'This is the last club ownership grant',
    body: 'Choose another person and give them the same club access before revoking this grant.',
  },
  CLUB_INVITATION_INVALID: {
    title: 'That club invitation cannot be used',
    body: 'Ask the club to send a new one. Invitations work once and expire after 14 days.',
  },
  CLUB_INVITATION_MAIL_FAILED: {
    title: 'The invitation email did not send',
    body: 'The invitation is saved. Resend it from the club screen.',
  },
  COVERAGE_REQUIRED: {
    title: 'Coverage has not started',
    body: 'Open this team and start coverage before writing to its scorebook.',
  },
  STAFF_HOLDS_GRANT: {
    title: 'That person already has club access to this team',
    body: 'Ask a coach on this team to add them as staff, or have them start coverage.',
  },
  TEAM_SLUG_TAKEN: {
    title: 'That address is taken',
    body: 'Another team in this club already uses it. Pick a different one.',
  },
  TEAM_GAME_IN_PROGRESS: {
    title: 'A game is still being scored',
    body: 'Finish that game in the scorebook, then remove the team.',
  },
  TEAM_ARCHIVE_LAST: {
    title: 'This is your only team',
    body: 'Removing it would lock you out of Good Season. Ask support to remove it, or add another team first.',
  },
  TEAM_RESTORE_BLOCKED: {
    title: 'That team cannot come back yet',
    body: 'Another active team in this club uses the address it had. Remove that team first, then restore this one.',
  },
  EVENT_GAME_IN_PROGRESS: {
    title: 'A game is being scored for this event',
    body: 'Finish it in the scorebook before hiding the event.',
  },
  EVENT_DRAFT_NO_GAME: {
    title: 'This event is still a draft',
    body: 'A scorekeeper could not open its game yet. Publish the event, then start the game.',
  },
  LOCATION_NAME_TAKEN: {
    title: 'That place name is taken',
    body: 'Another saved place on this team already has that name. Pick a different name, or edit that place instead.',
  },
  TEAM_INVITE_SIGN_IN_REQUIRED: {
    title: 'Sign in to finish this',
    body: 'That email already has a sign-in. Sign in with it, then open this invitation again.',
  },
  TEAM_INVITE_CONFIRMATION_REQUIRED: {
    title: 'Confirm that address',
    body: 'That email already has an account. Type it again to confirm and add a password, or sign in instead if it is already yours.',
  },

  GAME_FINALIZED: {
    title: 'This game is marked final',
    body: 'Reopen it to make a change.',
  },
  GAME_NOT_SUBMITTED: {
    title: 'This game was reopened',
    body: 'There is nothing to approve. It can be approved once it is finished again.',
  },
  EVENT_ALREADY_VOIDED: {
    title: 'That play was already corrected',
    body: 'Open the timeline to see the current version.',
  },
  SYNC_CONFLICT: {
    title: 'Two devices scored this game',
    body: 'Review the differences and pick which book is official.',
  },
  BATCH_TOO_LARGE: {
    // Reached from any route, not only a scorebook batch, so the body cannot
    // promise the outbox will resend.
    title: 'That is too big to send at once',
    body: 'Nothing is lost. Send it in smaller pieces.',
  },

  MEDIA_TOO_LARGE: {
    title: 'That image is too large',
    body: 'Crop it or choose a smaller one.',
  },
  MEDIA_TYPE_UNSUPPORTED: {
    title: 'That file type is not supported',
    body: 'Use a JPEG, PNG, or WebP.',
  },
  MEDIA_CONSENT_REQUIRED: {
    title: 'This photo is not cleared for sharing',
    body: 'Ask a guardian to approve public use before it leaves the team.',
  },
  MEDIA_TOO_SMALL: {
    title: 'That image is too small',
    body: 'Choose a larger photo.',
  },
  MEDIA_UNAVAILABLE: {
    title: 'Photos are not set up yet',
    body: 'Everything else works as usual. Contact support if you need photos sooner.',
  },

  // ADR-0084: only the per-recipient ceiling on DELIVERED setup emails. RATE_LIMITED
  // is every other ceiling, and its answer is to wait.
  SETUP_EMAIL_RECENTLY_SENT: {
    title: 'They were sent a setup email within the last hour',
    body: 'Ask them to check their inbox and spam folder, and send another once the hour is up if it has not arrived.',
  },
  SIGNUP_CODE_UNKNOWN: {
    title: 'That code does not match a team',
    body: 'Check the code with your coach and try again.',
  },
  SIGNUP_UNAVAILABLE: {
    title: 'This team is not taking signups',
    body: 'Ask your coach to open signup, then try the code again.',
  },
  SIGNUP_DAILY_LIMIT: {
    title: 'Too many signup emails today',
    body: 'Try again tomorrow, or ask your coach to add your family.',
  },
  SIGNUP_FULL: {
    title: 'Too many families are already waiting',
    body: 'Ask your coach to approve the families already waiting, then try again.',
  },
  SIGNUP_NOTICE_CHANGED: {
    title: 'The consent notice changed',
    body: 'Read the current notice and agree to that one before sending the form.',
  },
  SIGNUP_APPROVAL_EMPTY: {
    title: 'That approval adds nobody',
    body: 'Choose what happens to at least one child, then approve.',
  },
  SIGNUP_AWAITING_PARENT: {
    title: 'Some children are already added and waiting for a parent',
    body: 'Ask that parent to answer in Good Season. It closes on its own when they do.',
  },
  CONSENT_REQUIRED: {
    title: 'Waiting for a parent to agree',
    body: 'A parent has not agreed to the consent notice yet. Ask them to open Good Season and agree, then try again.',
  },
  OWN_CONSENT_REQUIRED: {
    title: 'Agree to the notice first',
    body: 'You have not agreed to the current parental consent notice. Open it, agree, then try again.',
  },
  RATE_LIMITED: {
    title: 'Too many tries in a row',
    body: 'Wait a few minutes and try again.',
  },
  MAIL_SEND_FAILED: {
    title: 'That message did not send',
    body: 'It is saved as a draft. Send it again when you are ready.',
  },
  UNAVAILABLE: {
    title: 'Not reachable right now',
    body: 'Anything recorded on this device is safe and will sync when the connection is back.',
  },
  INTERNAL: {
    title: 'That did not go through',
    body: 'Nothing was changed. Try it again.',
  },
};

/**
 * Copy for a code, or the general case when there is no code.
 *
 * The fallback is INTERNAL's copy rather than a second string, so there is one
 * sentence for "it did not go through" and it cannot drift from itself.
 *
 * The `??` is load-bearing and the type system will not tell you so. `ERROR_COPY`
 * is a `Record` over a literal union, and `noUncheckedIndexedAccess` does not
 * apply to those, so TypeScript types this lookup as always present. At runtime
 * an unrecognised code returns undefined and the caller throws on `.title`.
 *
 * That is not hypothetical. The API and the web bundle deploy separately, and a
 * service worker can serve a cached bundle for a long time. Deploy an API that
 * returns a code added since, and instead of the real message a coach gets the
 * ErrorBoundary's "this screen stopped" with a diagnostic that points at a
 * client crash rather than the server failure that caused it.
 *
 * The exhaustive `Record` still does its job: adding a code without writing copy
 * fails the build. It just cannot police what arrives over the wire.
 */
export function errorCopyFor(code?: ErrorCode): ErrorCopy {
  return ERROR_COPY[code ?? 'INTERNAL'] ?? ERROR_COPY.INTERNAL;
}
